"use client";

import { WithPermission } from "@atlas/composite";
import type { FileMetadata } from "@atlas/core";
import { useFormBuilder, useTranslation } from "@atlas/sdk";
import { CdvType } from "@enums";
import type { IHubCdv } from "@models";
import { CatalogMode } from "@modules";
import {
  HUB_CATALOG_TYPES_DEFAULT_SORT,
  useHubCatalogByVersion,
  useHubCatalogTypes,
  useHubCdv,
} from "@query";
import { getIconUrl, HUB_PERM } from "@shared";
import { useMemo } from "react";
import type { ICatalogDetailsFormValues } from "./CatalogDetailsForm";
import { CreateEditCatalogWizard } from "./CreateEditCatalogWizard";

const DEFAULT_FORM_VALUES: ICatalogDetailsFormValues = {
  typeId: "",
  label: "",
  description: "",
  overview: "",
  supportedCloud: [],
  documentationLocations: [],
  parentCatalogIds: [],
  tierId: "",
  addon: false,
  featured: false,
  icon: undefined as unknown as FileMetadata,
};

export function CreateEditCatalogPage({
  catalogId,
  version,
}: {
  catalogId?: number;
  version?: string;
}) {
  const { t } = useTranslation();
  const isEdit = Boolean(catalogId);
  const numericCatalogId = catalogId ?? 0;
  const catalogWizardRule = useMemo(
    () => (isEdit ? [HUB_PERM.CATALOG_EDIT] : [HUB_PERM.CATALOG_CREATE]),
    [isEdit],
  );

  const { data: catalog } = useHubCatalogByVersion(
    numericCatalogId,
    version ?? "",
    { enabled: isEdit && numericCatalogId > 0 && Boolean(version) },
  );

  const { data: existingFormData } = useFormBuilder(catalog?.formId ?? 0, {
    enabled: isEdit && (catalog?.formId ?? 0) > 0,
  });

  const { data: catalogTypes = [], isLoading: isCatalogTypesLoading } =
    useHubCatalogTypes({
      mode: "DATA",
      sort: [...HUB_CATALOG_TYPES_DEFAULT_SORT],
      startIndex: 0,
      limit: 100,
    });
  const { data: cdvData = [], isLoading: isCdvLoading } = useHubCdv();

  const tierOptions = useMemo(
    () =>
      (cdvData as IHubCdv[])
        .filter((cdv) => cdv.type === CdvType.Tier)
        .map((cdv) => ({
          value: String(cdv.id ?? 0),
          label: cdv.label || cdv.name || String(cdv.id ?? ""),
        })),
    [cdvData],
  );

  const cloudOptions = useMemo(
    () =>
      (cdvData as IHubCdv[])
        .filter((cdv) => cdv.type === CdvType.Cloud)
        .map((cdv) => ({
          value: cdv.name || cdv.label || String(cdv.id ?? ""),
          label: cdv.label || cdv.name || String(cdv.id ?? ""),
        })),
    [cdvData],
  );

  const cdvDefaultTierId = useMemo(() => {
    const defaultTier = (cdvData as IHubCdv[]).find(
      (cdv) => cdv.type === CdvType.Tier && cdv.isDefault,
    );
    return defaultTier?.id != null ? String(defaultTier.id) : "";
  }, [cdvData]);

  const cdvDefaultSupportedClouds = useMemo(
    () =>
      (cdvData as IHubCdv[])
        .filter((cdv) => cdv.type === CdvType.Cloud && cdv.isDefault)
        .map((cdv) => cdv.name || cdv.label || String(cdv.id ?? "")),
    [cdvData],
  );

  const iconUrl = useMemo(() => getIconUrl(catalog?.icon), [catalog?.icon]);

  const catalogFormValues = useMemo(():
    | ICatalogDetailsFormValues
    | undefined => {
    if (!isEdit || !catalog) return undefined;

    return {
      typeId: String(catalog.typeId ?? ""),
      label: catalog.label ?? "",
      description: catalog.description ?? "",
      token: catalog.token ?? "",
      overview: catalog.overview ?? "",
      supportedCloud: catalog.supportedCloud ?? [],
      documentationLocations: catalog.documentationLocations ?? [],
      parentCatalogIds: (catalog.parentCatalogIds ?? [])
        .map(String)
        .filter(
          (id) => catalog.catalogId == null || id !== String(catalog.catalogId),
        ),
      tierId: String(catalog.tierId ?? ""),
      addon: catalog.addon ?? false,
      featured: catalog.featured ?? false,
      icon: (iconUrl
        ? [{ url: iconUrl, name: "icon.png" }]
        : []) as unknown as FileMetadata,
    };
  }, [isEdit, catalog, iconUrl]);

  const formDefaultValues = useMemo((): ICatalogDetailsFormValues => {
    if (isEdit) return DEFAULT_FORM_VALUES;
    return {
      ...DEFAULT_FORM_VALUES,
      tierId: cdvDefaultTierId || "",
      supportedCloud: cdvDefaultSupportedClouds,
    };
  }, [isEdit, cdvDefaultTierId, cdvDefaultSupportedClouds]);

  const mode = isEdit ? CatalogMode.Edit : CatalogMode.Create;

  return (
    <WithPermission permissions={catalogWizardRule}>
      <CreateEditCatalogWizard
        isEdit={isEdit}
        numericCatalogId={numericCatalogId}
        mode={mode}
        catalog={catalog}
        existingFormData={existingFormData}
        catalogTypes={catalogTypes}
        tierOptions={tierOptions}
        cloudOptions={cloudOptions}
        isCatalogTypesLoading={isCatalogTypesLoading}
        isCdvLoading={isCdvLoading}
        formDefaultValues={formDefaultValues}
        catalogFormValues={catalogFormValues}
        t={t}
      />
    </WithPermission>
  );
}
